import { useMutation } from "@tanstack/react-query";
import { loginFn } from "@/modules/Auth/mutations/loginFn";
import { SubmitHandler, useForm } from "react-hook-form";
import { LoginFormData } from "@/modules/Auth/types/loginFormData";
import { zodResolver } from "@hookform/resolvers/zod";
import useAuth from "components/modules/Auth/useAuth";
import { useAuthRouter } from "./useAuthRouter";
import { toast } from "@/client/hooks/use-toast";
import useAccountMessages from "@/utils/useAccountMessages";
import useAuthValidationSchema from "@/modules/Auth/schemas/useAuthValidationSchema";
import { getQueryParam } from "@/utils/getQueryParam";

export const useLogin = () => {
    const { setToken, setUserEmail } = useAuth();
    const authRouter = useAuthRouter();
    const { loginSchema } = useAuthValidationSchema();
    const { getValMsg } = useAccountMessages();

    const {
        register,
        handleSubmit,
        setError,
        formState: { errors, isSubmitting },
    } = useForm<LoginFormData>({
        resolver: zodResolver(loginSchema),
        mode: "onBlur",
    });

    const mutation = useMutation({
        mutationFn: loginFn,
        retry: 1,
        onSuccess: (response) => {
            if (response.error) {
                setError("root", {
                    message: response.error.message,
                });
                toast({
                    title: getValMsg(response.error.messageKey, response.error.message).replace(
                        "durationInMin",
                        Math.ceil(parseInt(response.error.data?.remainingBlockDuration ?? "0") / 60),
                    ),
                    variant: "destructive",
                });
                return;
            }

            setToken(response.data.access_token);
            setUserEmail(response.data.email);
            const redirectUrl = getQueryParam("redirect");
            if (redirectUrl) {
                authRouter.replace(redirectUrl);
            } else {
                authRouter.replace("/dashboard");
            }
            toast({
                title: getValMsg("login-success"),
                variant: "success",
            });
        },
    });

    const onSubmit: SubmitHandler<LoginFormData> = async (data: LoginFormData) => {
        mutation.mutate(data);
    };

    const pending = mutation.isPending || isSubmitting;
    return { register, handleSubmit, onSubmit, errors, pending };
};
